import clsx from "clsx";
import React from "react";
import {
  Box,
  BoxProps,
  Typography,
  TextField,
  AutocompleteRenderInputParams,
  CircularProgress,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { ExerciseAsset } from "../exercise-library/types";
import { WorkoutExercise } from "./types";
import { defaultExerciseWithTitle, normalizeObsoleteExercise } from "./utils";
import { useAddExercise } from "../../hooks/useAddExercise";
import { ExerciseAutocomplete } from "../exercise-library/ExerciseAutocomplete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useCurrentUser, useUserIsClient } from "../../hooks/useCurrentUser";
import { AddedLozenge } from "../item/AddedLozenge";
import { SuggestExerciseContainer } from "./SuggestExerciseContainer";

const useStyles = makeStyles((theme) => ({
  root: {},

  workoutEditTitle: {
    color: theme.palette.secondary.main,
    textTransform: "capitalize",
    margin: `${theme.spacing(2.5, 0, 4, 0)}!important`,
  },

  label: {
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    fontSize: 16,
    margin: theme.spacing(2.5, 0, 2, 0),
  },

  input: {
    fontWeight: 500,
  },

  selectExerciseLabel: {
    color: theme.palette.text.primary,
    fontWeight: "500 !important" as any,
    lineHeight: "normal",
  },
  addToLibraryText: {
    color: theme.palette.text.secondary,
    lineHeight: "17px",
    marginTop: 20,
  },

  lozenge: {
    "&.MuiBox-root": { m: 0 },
    width: "fit-content",
  },
}));

export interface ExerciseDrawerSearchProps extends BoxProps {
  sectionName?: string | undefined;
  workoutExercise?: WorkoutExercise;
  onCreateNewExercise?: () => void;
  onExerciseSelect?: (exercise: WorkoutExercise) => void;
  readonly?: boolean;
  firstOpenFromSpreadsheet?: boolean;
  setFirstOpenFromSpreadsheet?: (value: boolean) => void;
  onSuggestExerciseComplete?: (exercise: WorkoutExercise) => void;
}

export function ExerciseDrawerSearch(props: ExerciseDrawerSearchProps) {
  const {
    className,
    sectionName,
    workoutExercise,
    onCreateNewExercise,
    onExerciseSelect,
    readonly = false,
    firstOpenFromSpreadsheet,
    setFirstOpenFromSpreadsheet,
    onSuggestExerciseComplete,
    ...other
  } = props;
  const s = useStyles();
  const [inputValue, setInputValue] = React.useState("");

  const [exerciseAsset, setExerciseAsset] =
    React.useState<ExerciseAsset | null>(null);

  const addExercise = useAddExercise();
  const isClient = useUserIsClient();

  const { coach } = useCurrentUser();

  const handleAutocompleteTitleChange = React.useCallback(
    (_, title: string) => {
      setInputValue(title);
    },
    [inputValue],
  );

  const handleAutocompleteChange = React.useCallback(
    (exerciseAsset: ExerciseAsset) => {
      if (!exerciseAsset) {
        return;
      }
      const sanitizedExercise = exerciseAsset?.[1];
      if (!sanitizedExercise) {
        return;
      }
      if (isClient && !sanitizedExercise.userId) {
        setExerciseAsset(sanitizedExercise);

        return;
      }
      const { assetId, exercise: exerciseFromLibrary } = sanitizedExercise;
      const resultExercise = exerciseFromLibrary;
      onExerciseSelect(
        normalizeObsoleteExercise({
          ...(workoutExercise ? workoutExercise : {}),
          title: resultExercise?.title,
          subtitle: resultExercise?.subtitle,
          instructions: resultExercise?.instructions,
          sets: resultExercise?.sets,
          images: resultExercise?.images,
          video: resultExercise?.video,
          typeExtraMeasurement: resultExercise?.typeExtraMeasurement,
          assetId,
        }),
      );
    },
    [inputValue, isClient],
  );

  const handleCreateNewExercise = React.useCallback(() => {
    if (!isClient) {
      onCreateNewExercise();
    } else {
      setExerciseAsset({
        exercise: {
          ...defaultExerciseWithTitle(inputValue),
        },
      });
      setInputValue("");
    }
  }, [inputValue, onCreateNewExercise, isClient]);

  const inputProps = {
    id: "workout-exercise-autocomplete",
    name: "title",
    placeholder: !exerciseAsset && "ex. Alternating bodyweight lunge",
    fullWidth: true,
  };

  const handleCloseAutocomplete = React.useCallback(
    (options: ExerciseAsset[]) => {
      const matchedOption = options.find(
        (option) => option.exercise.title.trim() === inputValue.trim(),
      );

      if (matchedOption) {
        onExerciseSelect(matchedOption.exercise);
      }
    },
    [inputValue],
  );

  return (
    <>
      <Box className={clsx(s.root, className)} {...other}>
        <Typography variant="h2" className={s.workoutEditTitle}>
          Add exercise
        </Typography>
        <Typography variant="h5">{sectionName}</Typography>

        <>
          <Typography variant="h6" className={s.label}>
            Choose exercise
          </Typography>
          <ExerciseAutocomplete
            renderCustomInput={(
              params: AutocompleteRenderInputParams,
              isLoading,
            ) => (
              <TextField
                {...params}
                {...inputProps}
                autoFocus={true}
                variant="outlined"
                disabled={addExercise}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    className: s.input,
                    endAdornment: isLoading && (
                      <CircularProgress
                        size={18}
                        sx={{ color: (theme) => theme.palette.text.primary }}
                      />
                    ),
                  },
                }}
              />
            )}
            limitTags={1}
            renderTags={(record: any, _1) => {
              return exerciseAsset ? (
                <AddedLozenge
                  className={s.lozenge}
                  text={exerciseAsset?.exercise?.title}
                  onRemoveClick={() => {
                    setExerciseAsset(null);
                  }}
                />
              ) : null;
            }}
            multiple
            fullWidth
            inputValue={inputValue}
            value={[inputValue] as any}
            onInputChange={handleAutocompleteTitleChange}
            onChange={handleAutocompleteChange}
            onCloseAutocomplete={handleCloseAutocomplete}
            onCreateNew={handleCreateNewExercise}
            autoFocus={false}
            disabled={!!exerciseAsset}
            open={inputValue.trim().length > 0}
            firstOpenFromSpreadsheet={firstOpenFromSpreadsheet}
            privacy="ALL"
            setFirstOpenFromSpreadsheet={setFirstOpenFromSpreadsheet}
          />
          <Box
            display="flex"
            gap={2}
            flexDirection="row"
            alignItems="center"
            mt={1}
          >
            {!exerciseAsset ? (
              <>
                <ArrowUpwardIcon sx={{ ml: 2 }} />
                <Typography className={s.selectExerciseLabel}>
                  Select an exercise
                </Typography>
              </>
            ) : (
              <SuggestExerciseContainer
                exerciseAsset={exerciseAsset}
                coachId={coach.id}
                onComplete={onSuggestExerciseComplete}
              />
            )}
          </Box>
        </>
      </Box>
    </>
  );
}
